import { Link } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useResendVerificationEmail } from "lib/hooks/api/auth/useResendVerificationEmail";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

// Why the user is being asked to confirm their address. Only the opening line differs: a social
// sign-in needs explaining (they didn't type an email, so an email step is unexpected), whereas
// after a sign-up form it's the obvious next step.
export type VerificationReason = "signup" | "social";

const HEADING: Record<VerificationReason, string> = {
  signup: "Check your email",
  social: "Confirm your email address",
};

/**
 * "Go and check your inbox" step, shared by the two flows that end in an unverified address:
 *  - email/password sign-up, where the server sends the link instead of signing the user in
 *  - social sign-in whose provider didn't vouch for the address, where the auth-service redirects
 *    here (see social-email-verification.ts in auth-service)
 *
 * Password reset does NOT use this card — BetterAuthForgotPasswordPage has its own confirmation
 * state instead, because this card names the address as fact ("We've sent an email to …"), which
 * would confirm the account exists. Better-auth deliberately doesn't reveal that, so neither can we.
 *
 * `email` is display + resend input, so it must be supplied by the caller rather than read from a
 * session — at this point in both flows there isn't one. `redirect` is passed on to the resend so a
 * resent link lands in the same place as the original.
 */
export function CheckYourEmailCard({
  email,
  reason = "signup",
  redirect,
}: {
  email: string;
  reason?: VerificationReason;
  redirect?: string;
}) {
  const {
    mutate: resendVerificationEmail,
    isLoading: isResending,
    isSuccess: hasResent,
    isError: isResendError,
    error: resendError,
  } = useResendVerificationEmail();

  return (
    <Page>
      <Card centred>
        <StotlesLogo />
        <Text h1>{HEADING[reason]}</Text>
        {reason === "social" && (
          <Text color="sysTextSecondary">
            Your sign-in provider didn&apos;t confirm your email address, so we need to check it
            ourselves before setting up your account.
          </Text>
        )}
        <Text>
          We&apos;ve sent an email to <strong>{email}</strong>. Open it and follow the link to
          continue.
        </Text>
        <Text color="sysTextSecondary">
          Didn&apos;t get it? Check your spam folder, then resend the link.
        </Text>
        <Text color="sysTextSecondary">This verification link will expire in 1 hour.</Text>
        <Button
          buttonType="secondary"
          fullWidth
          loading={isResending}
          disabled={hasResent}
          onClick={() => resendVerificationEmail({ email, redirect })}
        >
          {hasResent ? "Verification email sent" : "Resend verification email"}
        </Button>
        {isResendError && (
          <Text color="sysDestructiveDefault">
            {betterAuthErrorText(resendError, "Could not resend verification email")}
          </Text>
        )}
        <Text color="sysTextSecondary">
          <Link to="/auth/login">Back to log in</Link>
        </Text>
      </Card>
    </Page>
  );
}
